/**
 * Navigation icons.
 *
 * Drawn to the approved interface study's hand: a 24-unit grid, 1.7 stroke,
 * round caps and joins, no fill. They are authored here rather than pulled from
 * an icon library so the set stays exactly this size — twelve glyphs, a few
 * hundred bytes — and so the stroke weight cannot drift when someone adds the
 * thirteenth from a different family.
 *
 * Every icon is `aria-hidden`. The navigation label beside it is the accessible
 * name, and a screen reader announcing "cube, laboratory work" reads the
 * drawing out loud, which helps nobody.
 */

export interface NavIconProps {
  readonly size?: number;
  readonly className?: string;
}

function Icon({
  size = 17,
  className,
  children,
}: NavIconProps & { readonly children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
      style={{ flex: 'none' }}
    >
      {children}
    </svg>
  );
}

/** The laboratory mark: a flask. Used for the product, never for a tenant. */
export function IconFlask(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M9.5 3v5.6L4.6 17a2.2 2.2 0 0 0 1.9 3.4h11a2.2 2.2 0 0 0 1.9-3.4l-4.9-8.4V3" />
      <path d="M8.2 3h7.6" />
      <path d="M7 14.5h10" />
    </Icon>
  );
}

export function IconDashboard(props: NavIconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Icon>
  );
}

/** A person's own work list: a clipboard with two ticked lines. */
export function IconTasks(props: NavIconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="3.5" width="16" height="17" rx="2" />
      <path d="M8 9l1.5 1.5L12 8" />
      <path d="M8 15l1.5 1.5L12 14" />
      <path d="M14.5 9.5h2" />
      <path d="M14.5 15.5h2" />
    </Icon>
  );
}

/** The equipment register: a balance. */
export function IconEquipment(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M12 4v16" />
      <path d="M7.5 20h9" />
      <path d="M5 7h14" />
      <path d="M5 7l-2.5 5.5a2.5 2.5 0 0 0 5 0z" />
      <path d="M19 7l-2.5 5.5a2.5 2.5 0 0 0 5 0z" />
    </Icon>
  );
}

export function IconClients(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M16 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" />
      <circle cx="9" cy="7" r="3.2" />
      <path d="M22 20v-1.5a4 4 0 0 0-3-3.87" />
      <path d="M16.5 4.2a3.2 3.2 0 0 1 0 5.6" />
    </Icon>
  );
}

export function IconProjects(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M3 7.5A2 2 0 0 1 5 5.5h3.6l1.9 2.2H19a2 2 0 0 1 2 2v8.3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    </Icon>
  );
}

export function IconCatalogue(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H18a2 2 0 0 1 2 2v13a1 1 0 0 1-1 1H5.5A1.5 1.5 0 0 1 4 18.5Z" />
      <path d="M8.5 8.5h7M8.5 12h7M8.5 15.5h4" />
    </Icon>
  );
}

export function IconQuotations(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M20.6 12.4 12.4 20.6a1.9 1.9 0 0 1-2.7 0l-6.3-6.3a1.9 1.9 0 0 1-.5-1.6l.9-6.2a1.9 1.9 0 0 1 1.6-1.6l6.2-.9a1.9 1.9 0 0 1 1.6.5l6.3 6.3a1.9 1.9 0 0 1 0 2.7Z" />
      <circle cx="8.4" cy="8.4" r="1.3" />
    </Icon>
  );
}

export function IconPricing(props: NavIconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" />
      <path d="M2.5 10h19" />
      <path d="M6 14.5h4" />
    </Icon>
  );
}

/** Billing: the work-order services waiting to be invoiced (Phase 11). */
export function IconBilling(props: NavIconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="4" width="18" height="15" rx="2" />
      <path d="M3 9h18" />
      <path d="M7 13.5h4" />
      <path d="m14.5 13.8 1.4 1.4 2.6-2.8" />
    </Icon>
  );
}

/** Invoices: a document with a number and its lines (Phase 11). */
export function IconInvoices(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3h9l4 4v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M15 3v4h4" />
      <path d="M8 12h8M8 16h5" />
    </Icon>
  );
}

/** Payments: money received (Phase 11). */
export function IconPayments(props: NavIconProps) {
  return (
    <Icon {...props}>
      <rect x="2.5" y="6" width="19" height="12" rx="2.2" />
      <path d="M2.5 10.3h19" />
      <circle cx="17" cy="14.3" r="1.6" />
    </Icon>
  );
}

export function IconWorkOrders(props: NavIconProps) {
  return (
    <Icon {...props}>
      <rect x="5" y="4" width="14" height="17" rx="2.2" />
      <path d="M9 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1" />
      <path d="M9 11h6M9 15h4" />
    </Icon>
  );
}

/** Reserved for the samples register (Phase 7). */
export function IconSamples(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="m12 3 8.5 4.6L12 12.2 3.5 7.6 12 3Z" />
      <path d="m3.5 12.4 8.5 4.6 8.5-4.6" />
      <path d="m3.5 16.8 8.5 4.6 8.5-4.6" />
    </Icon>
  );
}

/** Sample receipts: a handover slip with a torn edge (D213). */
export function IconReceipts(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M6 3h12v18l-2-1.5-2 1.5-2-1.5-2 1.5-2-1.5L6 21V3Z" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </Icon>
  );
}

/** Reserved for review and approval (Phase 9). */
export function IconReview(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M9.5 12.4 11.4 14.3 15 10.7" />
      <path d="M12 3l2.3 1.7 2.8-.2.9 2.7 2.3 1.6-1 2.7 1 2.7-2.3 1.6-.9 2.7-2.8-.2L12 21l-2.3-1.7-2.8.2-.9-2.7L3.7 15.2l1-2.7-1-2.7 2.3-1.6.9-2.7 2.8.2Z" />
    </Icon>
  );
}

/**
 * Final approval (Phase 9): a seal with a mark inside it.
 *
 * A SEAL, NOT A GRADE. The glyph says "a second person signed this record off",
 * which is exactly what an approval is; it carries no tick-in-a-green-circle
 * reading of "the material passed" (decision D104).
 */
export function IconApproval(props: NavIconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="9.5" r="6" />
      <path d="m9.4 9.6 1.9 1.9 3.3-3.6" />
      <path d="M8.4 14.6 7.2 21l4.8-2.2 4.8 2.2-1.2-6.4" />
    </Icon>
  );
}

/**
 * Work that came back (Phase 9): a record with a cycle mark.
 *
 * The cycle is drawn as a closed loop rather than a pointing arrow, so nothing
 * here has to be mirrored between Arabic and English.
 */
export function IconReturned(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
      <path d="M14 3v5h5" />
      <circle cx="12" cy="14" r="3.2" />
      <path d="M12 10.8v1.6" />
    </Icon>
  );
}

/** Reserved for reports (Phase 10). */
export function IconReports(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h4" />
    </Icon>
  );
}

export function IconUsers(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M12 21s7-3.2 7-9V5.8L12 3 5 5.8V12c0 5.8 7 9 7 9Z" />
      <path d="m9.2 11.8 2 2 3.6-3.6" />
    </Icon>
  );
}

export function IconSettings(props: NavIconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 14.5a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.56V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06A2 2 0 1 1 3.9 16.9l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1H2.5a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.56-1.1 1.7 1.7 0 0 0-.34-1.87L3.76 7A2 2 0 1 1 6.6 4.16l.06.06a1.7 1.7 0 0 0 1.87.34H8.6a1.7 1.7 0 0 0 1-1.56V2.5a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06A2 2 0 1 1 20.36 6.6l-.06.06a1.7 1.7 0 0 0-.34 1.87v.07a1.7 1.7 0 0 0 1.56 1h.14a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.56 1Z" />
    </Icon>
  );
}

export function IconAudit(props: NavIconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.2V12l3 1.8" />
    </Icon>
  );
}

export function IconStorage(props: NavIconProps) {
  return (
    <Icon {...props}>
      <ellipse cx="12" cy="6.2" rx="7.5" ry="2.9" />
      <path d="M4.5 6.2v11.6c0 1.6 3.4 2.9 7.5 2.9s7.5-1.3 7.5-2.9V6.2" />
      <path d="M4.5 12c0 1.6 3.4 2.9 7.5 2.9s7.5-1.3 7.5-2.9" />
    </Icon>
  );
}

export function IconProfile(props: NavIconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="8" r="3.6" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </Icon>
  );
}

/**
 * A bin, for the row action that takes something off a list ("Remove
 * service"). Drawn on the same grid and stroke as the navigation set.
 */
export function IconRemove(props: NavIconProps) {
  return (
    <Icon {...props}>
      <path d="M4 7h16" />
      <path d="M9.5 7V4.5h5V7" />
      <path d="M6.5 7l1 12.5h9l1-12.5" />
      <path d="M10.5 11v5.5M13.5 11v5.5" />
    </Icon>
  );
}
